/*
 * RGM Reels — Phase 1B — Create Reel experience
 * ================================================
 *
 * Mobile-first creator flow built on top of the Phase 1A backend:
 *   • POST /api/upload/video        — Reel media upload
 *   • POST /api/reels                — Reel creation (entitlement-gated)
 *   • GET  /api/reels/me/picker/my-tracks — owner-only My Tracks picker
 *
 * The server is authoritative for every ownership + tier + rights
 * check. This UI never claims entitlement locally — it only hides /
 * disables controls the user clearly can't use so the UX stays clean,
 * and reports the backend's 402 / 403 / 400 messages verbatim.
 *
 * No Beat Marketplace reuse. No gifting / comments / likes / shares
 * wired here (Phase 1C/D). No video editing, trimming, filters.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";
import { useTier } from "@/hooks/useTier";

const STEPS = {
  PICK_MODE: "pick_mode",
  FREESTYLE: "freestyle",
  USE_MY_TRACK_PICK: "use_my_track_pick",
  USE_MY_TRACK_CAPTURE: "use_my_track_capture",
  UPLOAD_VIDEO: "upload_video",
  PHOTO_MUSIC: "photo_music",
  PREVIEW: "preview",
  DONE: "done",
};

// Each Reel type → the Phase 1A FeatureKey server-gates on. We mirror
// the lock locally only to hide/dim unavailable tiles — the API still
// enforces every call independently.
const TILE_DEFS = [
  {
    key: "freestyle",
    icon: "🎤",
    title: "Record Freestyle",
    sub: "Camera + mic • free for every artist",
    feature: "reel_record_freestyle",
    reelType: "recorded_freestyle",
  },
  {
    key: "use_my_track",
    icon: "🎵",
    title: "Use My Track",
    sub: "Perform over your own RGM song",
    feature: "reel_performance_own_track",
    reelType: "performance_with_track",
  },
  {
    key: "upload_video",
    icon: "📱",
    title: "Upload Video",
    sub: "Share a finished video from your phone",
    feature: "reel_upload_video",
    reelType: "uploaded_video",
  },
  {
    key: "photo_music",
    icon: "📸",
    title: "Photo + Music",
    sub: "Promo card with your RGM track",
    feature: "reel_image_with_music",
    reelType: "image_with_audio",
  },
];

// Best mimeType supported by this browser for recording. Mirrors the
// pattern used by studioEngine.js for the audio recorder — iOS Safari
// prefers video/mp4, everything else prefers webm.
function pickRecorderMime() {
  if (typeof MediaRecorder === "undefined") return null;
  const candidates = [
    "video/mp4;codecs=avc1,mp4a",
    "video/mp4",
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
  ];
  for (const m of candidates) {
    try { if (MediaRecorder.isTypeSupported(m)) return m; } catch (_) { /* older */ }
  }
  return null;
}

// Translate a mime hint into an extension so the backend's MIME
// allowlist (.mp4|.mov|.webm|.m4v) accepts the upload.
function extForMime(mime) {
  if (!mime) return ".webm";
  if (/mp4/i.test(mime)) return ".mp4";
  if (/webm/i.test(mime)) return ".webm";
  if (/quicktime|mov/i.test(mime)) return ".mov";
  return ".webm";
}

// ─────────────────────────────────────────────────────────────────
// Freestyle recorder — reused by `freestyle` and
// `use_my_track_capture` steps. Mobile-first, iOS-safe.
// ─────────────────────────────────────────────────────────────────
function FreestyleRecorder({ onFinish, onCancel }) {
  const videoRef = useRef(null);
  const reviewRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const tickRef = useRef(null);
  const [facing, setFacing] = useState("user");
  const [permission, setPermission] = useState("prompt"); // prompt|granted|denied
  const [recording, setRecording] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [reviewBlob, setReviewBlob] = useState(null);
  const [reviewUrl, setReviewUrl] = useState(null);
  // iPhone QA #3 — Retake session counter. The live <video> is keyed
  // by this so React unmounts/remounts a fresh DOM node every time
  // the user tapes Retake, which prevents Safari from retaining the
  // previous review Blob source on the reused element. Incrementing
  // this also triggers the camera-reacquire useEffect so we get a
  // brand-new live MediaStream bound to the fresh element.
  const [sessionSeq, setSessionSeq] = useState(0);
  const [mime] = useState(() => pickRecorderMime());

  // Mirror the live reviewUrl into a ref so the unmount-only cleanup
  // can revoke the latest value without needing reviewUrl in its
  // dep list (which would re-run the cleanup on every change).
  const latestReviewUrlRef = useRef(null);
  useEffect(() => { latestReviewUrlRef.current = reviewUrl; }, [reviewUrl]);

  const stopStream = useCallback(() => {
    try { streamRef.current?.getTracks()?.forEach(t => t.stop()); } catch (_) {/*noop*/}
    streamRef.current = null;
  }, []);

  const openCamera = useCallback(async (desiredFacing) => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setPermission("denied");
      toast.error("This browser can't access the camera");
      return;
    }
    stopStream();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: desiredFacing || facing },
        audio: true,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true;
        videoRef.current.playsInline = true;
        try { await videoRef.current.play(); } catch (_) { /* ios autoplay */ }
      }
      setPermission("granted");
    } catch (e) {
      setPermission("denied");
      toast.error(e?.name === "NotAllowedError"
        ? "Camera permission denied"
        : "Could not open camera");
    }
  }, [facing, stopStream]);

  useEffect(() => {
    // iPhone QA #3 — reacquire camera on initial mount, facingMode
    // change AND every Retake (sessionSeq bump). Running openCamera
    // after the live <video> remounts guarantees we bind a fresh
    // MediaStream to the fresh DOM node — Safari was retaining the
    // old review Blob source on the reused element otherwise.
    if (!reviewUrl) openCamera(facing);
  }, [facing, openCamera, sessionSeq, reviewUrl]);

  // Unmount-only cleanup. Deliberately dependency-free so the
  // cleanup fires exactly once (on unmount) instead of running on
  // every `reviewUrl` change — the earlier wiring was tearing the
  // live camera stream down the moment recording finished because
  // the dep-change cleanup ran mid-finalisation. We now keep the
  // review Blob URL alive for the entire review session, revoking
  // it only when the whole recorder unmounts.
  // iPhone QA #4 — force Safari to preload the Blob URL on review
  // mount. Without this, iOS Safari's native Play tap on a <video
  // src=blob:…> sometimes fails silently because the element never
  // left HAVE_NOTHING. load() nudges it to HAVE_METADATA, after
  // which Play works as expected.
  useEffect(() => {
    if (!reviewUrl || !reviewRef.current) return;
    try { reviewRef.current.load(); } catch (_) { /* noop */ }
  }, [reviewUrl]);

  useEffect(() => () => {
    try { streamRef.current?.getTracks()?.forEach(t => t.stop()); } catch (_) {/*noop*/}
    streamRef.current = null;
    if (tickRef.current) clearInterval(tickRef.current);
    try { if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop(); } catch (_) {/*noop*/}
    // Grab the latest reviewUrl via ref so this cleanup can read it
    // without having it in deps (which would re-run on every change).
    const url = latestReviewUrlRef.current;
    if (url) URL.revokeObjectURL(url);
  }, []);

  const flipCamera = () => setFacing(f => f === "user" ? "environment" : "user");

  const beginCountdown = () => {
    if (recording || countdown > 0 || reviewBlob) return;
    if (!mime) {
      toast.error("Recording not supported on this browser");
      return;
    }
    let n = 3;
    setCountdown(n);
    const iv = setInterval(() => {
      n -= 1;
      if (n <= 0) {
        clearInterval(iv);
        setCountdown(0);
        startRecording();
      } else {
        setCountdown(n);
      }
    }, 900);
  };

  const startRecording = () => {
    if (!streamRef.current) return;
    // iPhone QA #2 defensive check — if the live MediaStream has lost
    // its video track by now (facingMode re-request raced the user
    // tapping Start, hardware release, etc.) MediaRecorder will
    // happily produce an audio-only file. Bail with a clear message
    // instead of silently recording audio-only.
    const videoTracks = streamRef.current.getVideoTracks();
    const liveVideoTrack = videoTracks.find(t => t.readyState === "live" && t.enabled !== false);
    if (!liveVideoTrack) {
      toast.error("Camera track is not ready — try Flip or reload.");
      return;
    }
    try {
      chunksRef.current = [];
      let dataAvailableCount = 0;
      const rec = new MediaRecorder(streamRef.current, mime ? { mimeType: mime } : undefined);
      rec.ondataavailable = (e) => {
        dataAvailableCount += 1;
        if (e.data?.size) chunksRef.current.push(e.data);
      };
      const finaliseBlob = () => {
        // Snapshot the chunk count BEFORE we null the array, so the
        // dev-only diagnostic reports the real number of chunks the
        // recorder produced (previous version logged 0 because the
        // array was already cleared).
        const chunkCount = chunksRef.current.length;
        const actualType = rec.mimeType || mime || "video/mp4";
        const blob = new Blob(chunksRef.current, { type: actualType });
        chunksRef.current = [];
        if (process.env.NODE_ENV !== "production") {
          try { console.log("[reel-recorder] blob", {
            type: blob.type, size: blob.size,
            chunks: chunkCount,
            dataAvailableEvents: dataAvailableCount,
            recMime: rec.mimeType, pickedMime: mime,
          }); } catch (_) {/*noop*/}
        }
        setReviewBlob(blob);
        setReviewUrl(URL.createObjectURL(blob));
        if (tickRef.current) clearInterval(tickRef.current);
        setRecording(false);
      };
      rec.onstop = finaliseBlob;
      // iPhone QA #5 ROOT-CAUSE FIX — do NOT call requestData() nor
      // pass a timeslice. On iOS Safari calling requestData() before
      // stop() forces MediaRecorder to emit an intermediate
      // fragmented-MP4 chunk BEFORE the terminal one; concatenating
      // the two in a single Blob yields the same fragmented-MP4
      // problem QA #2 was supposed to kill (video decoder fails,
      // audio decoder still works → black video / unavailable-media
      // icon in Safari). MediaRecorder guarantees that stop() emits
      // one terminal dataavailable with every buffered byte, so
      // dropping requestData() keeps the recording single-fragment.
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
      setElapsed(0);
      tickRef.current = setInterval(() => {
        setElapsed(e => {
          const next = e + 1;
          // Hard safety stop at 60s (Phase 1 limit).
          if (next >= 60) stopRecording();
          return next;
        });
      }, 1000);
    } catch (e) {
      toast.error("Could not start recording");
      setRecording(false);
    }
  };

  const stopRecording = () => {
    try {
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        // iPhone QA #5 — DO NOT call requestData() here. On iOS
        // Safari requestData() causes an intermediate fragment to be
        // emitted alongside the terminal stop() fragment, producing
        // the same fragmented-MP4 unplayability that QA #2 fought
        // off. stop() alone guarantees one complete terminal
        // dataavailable with every buffered byte.
        recorderRef.current.stop();
      }
    } catch (_) { /* noop */ }
  };

  const retake = () => {
    // iPhone QA #3 — deterministic Retake lifecycle:
    //   1. pause the review <video> and detach its src so Safari
    //      releases the Blob decoder immediately,
    //   2. revoke the Blob URL exactly once,
    //   3. clear recorder references + review state,
    //   4. fully release the old MediaStream tracks (iOS Safari
    //      marks them as ended after stop() and reusing them yields
    //      the "unavailable video" play-with-slash icon),
    //   5. bump sessionSeq so the live <video> remounts AND the
    //      camera-reacquire useEffect fires with a fresh node.
    try {
      const r = reviewRef.current;
      if (r) {
        try { r.pause(); } catch (_) {/*noop*/}
        try { r.removeAttribute("src"); r.load(); } catch (_) {/*noop*/}
      }
    } catch (_) { /*noop*/ }
    if (reviewUrl) URL.revokeObjectURL(reviewUrl);
    try {
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stop();
      }
    } catch (_) {/*noop*/}
    recorderRef.current = null;
    chunksRef.current = [];
    setReviewBlob(null);
    setReviewUrl(null);
    setElapsed(0);
    // Release the exhausted MediaStream — iOS Safari may have ended
    // the video track as part of MediaRecorder.stop(). The useEffect
    // bound to [sessionSeq] below reacquires a fresh stream against
    // the newly-mounted live <video>.
    stopStream();
    setSessionSeq(s => s + 1);
  };

  const useRecording = () => {
    if (!reviewBlob) return;
    const ext = extForMime(reviewBlob.type);
    const file = new File([reviewBlob], `freestyle_${Date.now()}${ext}`, { type: reviewBlob.type });
    stopStream();
    onFinish?.({ file, durationSeconds: elapsed });
  };

  return (
    <div className="w-full max-w-md mx-auto" data-testid="freestyle-recorder">
      {/* Phase 1B iPhone QA — the viewport is capped to 60vh and
          max-height 460px so Start/Stop/Flip/Retake/Continue sit
          comfortably above Safari's bottom browser chrome. 9:16 ratio
          preserved via aspect-ratio AND bounded by max-h to prevent
          the box from eating the whole screen on tall iPhone layouts. */}
      <div className="relative w-full mx-auto aspect-[9/16] max-h-[min(60vh,460px)] bg-black rounded-xl overflow-hidden border border-purple-500/30">
        {reviewUrl ? (
          <video
            key={reviewUrl}
            ref={reviewRef}
            src={reviewUrl}
            controls
            playsInline
            preload="auto"
            // Phase 1B iPhone QA #4 — the review element now uses
            // Safari's default native controls so the Play button
            // behaves normally on iPhone. The aggressive attributes
            // we added in QA #1 (disablePictureInPicture,
            // controlsList="…noremoteplayback", x-webkit-airplay=deny)
            // interfered with Safari's native Play UI once the
            // single-shot MP4 fix in QA #2 made loadedmetadata reliable.
            // The original InvalidStateError they guarded against is
            // no longer reproducible because the Blob is now a
            // complete MP4 (full moov atom) — loadedmetadata fires
            // before any PiP tap can race.
            onLoadedMetadata={(e) => {
              if (process.env.NODE_ENV !== "production") {
                const v = e.target;
                try { console.log("[reel-recorder] review loadedmetadata", {
                  videoWidth: v.videoWidth, videoHeight: v.videoHeight,
                  duration: v.duration, readyState: v.readyState,
                }); } catch (_) {/*noop*/}
              }
            }}
            className="w-full h-full object-contain bg-black"
            data-testid="freestyle-review-video"
          />
        ) : (
          <>
            <video
              key={`live-${sessionSeq}`}
              ref={videoRef}
              className="w-full h-full object-cover"
              muted
              playsInline
              disablePictureInPicture
              controlsList="nodownload noplaybackrate noremoteplayback"
              data-testid="freestyle-live-video"
            />
            {countdown > 0 && (
              <div
                className="absolute inset-0 flex items-center justify-center bg-black/40"
                data-testid="freestyle-countdown"
              >
                <span className="text-7xl font-black text-white drop-shadow-lg">{countdown}</span>
              </div>
            )}
            {recording && (
              <div
                className="absolute top-3 left-3 flex items-center gap-2 bg-red-600/90 rounded-full px-3 py-1 text-white text-xs font-black"
                data-testid="freestyle-rec-pill"
              >
                <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                REC {String(Math.floor(elapsed / 60)).padStart(2, "0")}:{String(elapsed % 60).padStart(2, "0")}
              </div>
            )}
            {permission === "denied" && (
              <div
                className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center text-white bg-black/70"
                data-testid="freestyle-denied"
              >
                <span className="text-sm">Camera permission is required to record a freestyle.</span>
                <Button
                  size="sm" variant="outline"
                  onClick={() => openCamera(facing)}
                  className="mt-3 text-white border-gray-500"
                  data-testid="freestyle-retry-camera"
                >Try again</Button>
              </div>
            )}
          </>
        )}
      </div>
      <div className="flex items-center justify-between mt-3 gap-2">
        <Button
          variant="outline"
          onClick={() => { stopStream(); onCancel?.(); }}
          className="text-white border-gray-700"
          data-testid="freestyle-cancel"
        >Cancel</Button>
        {!reviewBlob && !recording && (
          <>
            <Button
              variant="outline"
              onClick={flipCamera}
              disabled={permission !== "granted"}
              className="text-white border-gray-700"
              data-testid="freestyle-flip-camera"
            >Flip</Button>
            <Button
              onClick={beginCountdown}
              disabled={permission !== "granted" || countdown > 0}
              className="bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-400 hover:to-pink-500 text-white font-black"
              data-testid="freestyle-start"
            >● Start Recording</Button>
          </>
        )}
        {recording && (
          <Button
            onClick={stopRecording}
            className="bg-white text-red-600 font-black"
            data-testid="freestyle-stop"
          >■ Stop</Button>
        )}
        {reviewBlob && (
          <>
            <Button
              variant="outline"
              onClick={retake}
              className="text-white border-gray-700"
              data-testid="freestyle-retake"
            >Retake</Button>
            <Button
              onClick={useRecording}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-black"
              data-testid="freestyle-continue"
            >Continue →</Button>
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Lock badge — rendered on tiles the current tier can't use.
// ─────────────────────────────────────────────────────────────────
function LockBadge({ onUpgrade }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onUpgrade?.(); }}
      className="absolute top-2 right-2 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-yellow-500/20 border border-yellow-400/50 text-yellow-100 font-black"
      data-testid="tile-lock-badge"
    >Premium</button>
  );
}

// ─────────────────────────────────────────────────────────────────
// My Tracks picker — surfaces only owner-controlled tracks via
// GET /api/reels/me/picker/my-tracks.
// ─────────────────────────────────────────────────────────────────
function MyTracksPicker({ onPick, onCancel }) {
  const [tracks, setTracks] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/reels/me/picker/my-tracks", { params: { limit: 50 } })
      .then((r) => { if (!cancelled) setTracks(r.data?.tracks || []); })
      .catch((e) => {
        if (cancelled) return;
        const code = e?.response?.status;
        if (code === 401) setErr("Please sign in to see your tracks.");
        else setErr(e?.response?.data?.detail || "Could not load your tracks.");
      });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="w-full max-w-md mx-auto" data-testid="my-tracks-picker">
      <h3 className="text-white text-base font-black mb-3">Pick one of your RGM tracks</h3>
      {err && <div className="text-amber-300 text-sm mb-2" data-testid="my-tracks-error">{err}</div>}
      {tracks === null && !err && (
        <div className="text-gray-400 text-sm">Loading your tracks…</div>
      )}
      {tracks && tracks.length === 0 && (
        <div className="text-gray-400 text-sm" data-testid="my-tracks-empty">
          You haven't uploaded any RGM tracks yet. Upload a single first, then come back.
        </div>
      )}
      <div className="grid grid-cols-1 gap-2">
        {(tracks || []).map(t => (
          <button
            key={t.submission_id}
            type="button"
            onClick={() => onPick(t)}
            className="flex items-center gap-3 bg-black/50 border border-gray-800 hover:border-purple-500/60 rounded-lg p-3 text-left text-white"
            data-testid={`my-track-${t.submission_id}`}
          >
            <div className="w-12 h-12 rounded bg-gray-900 overflow-hidden flex-shrink-0">
              {t.cover_art
                ? <img src={t.cover_art} alt="" className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center text-gray-600">♪</div>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold truncate">{t.track_title || "Untitled"}</div>
              <div className="text-xs text-gray-400 truncate">{t.artist_name || ""}</div>
            </div>
          </button>
        ))}
      </div>
      <div className="mt-3">
        <Button variant="outline" onClick={onCancel} className="text-white border-gray-700" data-testid="my-tracks-cancel">
          Back
        </Button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────
export default function CreateReelPage() {
  const navigate = useNavigate();
  const { hasFeature, tier } = useTier();
  // iPhone QA #5 — ref on the Review & Post preview video so we can
  // force Safari to preload metadata on the Blob URL once the step
  // transitions in. Mirrors the recorder-review nudge from QA #4.
  const previewVideoRef = useRef(null);

  // Anonymous visitors never pass `hasFeature` because `/me/tier` is
  // authenticated. We detect "signed-out" via localStorage so the
  // Freestyle tile shows a "Sign in to create" affordance rather than
  // a misleading "Premium" badge (Freestyle is a free feature).
  const isSignedIn = (() => {
    try { return !!localStorage.getItem("session_token"); } catch (_) { return false; }
  })();

  const [step, setStep] = useState(STEPS.PICK_MODE);
  const [mode, setMode] = useState(null); // one of TILE_DEFS[].key

  // Draft that collects everything until Post Reel.
  const [draft, setDraft] = useState({
    reelType: null,
    media: null,          // { file: File, previewUrl: string, kind: 'video'|'image' }
    uploaded: null,       // { url, public_id, poster_url, duration_seconds }
    attachedTrack: null,  // { submission_id, track_title, artist_name, cover_art }
    artistAudio: {        // optional artist-provided audio (freestyle / photo-music / uploaded_video)
      enabled: false,
      file: null,
      url: null,
      publicId: null,
      ack: false,
    },
    caption: "",
  });
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [posting, setPosting] = useState(false);
  const [postedReel, setPostedReel] = useState(null);
  const [myReels, setMyReels] = useState(null);

  // ---- helpers ---------------------------------------------------
  const reset = () => {
    if (draft.media?.previewUrl) URL.revokeObjectURL(draft.media.previewUrl);
    setDraft({
      reelType: null, media: null, uploaded: null, attachedTrack: null,
      artistAudio: { enabled: false, file: null, url: null, publicId: null, ack: false },
      caption: "",
    });
    setUploading(false);
    setUploadPct(0);
    setPosting(false);
  };

  const goHome = () => { reset(); setMode(null); setStep(STEPS.PICK_MODE); };

  // Pick a mode from the launcher. Entitlement-locked tiles bounce to
  // /membership; signed-out visitors bounce to /login first.
  const pickMode = (tile) => {
    if (!isSignedIn) {
      navigate("/login?next=/create/reel");
      return;
    }
    if (!hasFeature(tile.feature)) {
      navigate("/membership?from=reels");
      return;
    }
    setMode(tile.key);
    setDraft(d => ({ ...d, reelType: tile.reelType }));
    if (tile.key === "freestyle")       setStep(STEPS.FREESTYLE);
    else if (tile.key === "use_my_track") setStep(STEPS.USE_MY_TRACK_PICK);
    else if (tile.key === "upload_video") setStep(STEPS.UPLOAD_VIDEO);
    else if (tile.key === "photo_music")  setStep(STEPS.PHOTO_MUSIC);
  };

  // Shared video upload — pushes a File to /api/upload/video and
  // stashes {url, public_id, poster_url} on the draft.
  const uploadVideoFile = async (file, purpose) => {
    if (!file) return null;
    setUploading(true);
    setUploadPct(0);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("purpose", purpose);
      const res = await api.post("/upload/video", form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setUploadPct(Math.round((e.loaded / e.total) * 100));
        },
      });
      const data = res.data || {};
      return {
        url: data.url, public_id: data.public_id,
        poster_url: data.poster_url, duration_seconds: data.duration_seconds,
      };
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Upload failed"));
      return null;
    } finally {
      setUploading(false);
      setUploadPct(0);
    }
  };

  const uploadImageFile = async (file) => {
    if (!file) return null;
    setUploading(true);
    setUploadPct(0);
    try {
      const form = new FormData();
      form.append("file", file);
      // ImageUploader uses `general`; Reels image_with_audio keeps the
      // image on the existing image purpose set — "general" is the
      // intentional fallback already allowed by ALLOWED_IMAGE_PURPOSES.
      form.append("purpose", "general");
      const res = await api.post("/upload/image", form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setUploadPct(Math.round((e.loaded / e.total) * 100));
        },
      });
      return { url: res.data?.url, public_id: res.data?.public_id };
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Upload failed"));
      return null;
    } finally {
      setUploading(false);
      setUploadPct(0);
    }
  };

  // ---- flow transitions -----------------------------------------
  const onFreestyleFinish = async ({ file, durationSeconds }) => {
    // iOS Safari-safe: carry blob preview locally; upload fires later
    // (we upload at step=preview "Post Reel" time so retakes don't
    // waste Cloudinary bandwidth).
    const previewUrl = URL.createObjectURL(file);
    setDraft(d => ({
      ...d,
      media: { file, previewUrl, kind: "video", duration: durationSeconds },
    }));
    setStep(STEPS.PREVIEW);
  };

  const onUploadVideoChosen = async (file) => {
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    setDraft(d => ({
      ...d,
      media: { file, previewUrl, kind: "video" },
    }));
    setStep(STEPS.PREVIEW);
  };

  const onUploadImageChosen = async (file) => {
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    setDraft(d => ({
      ...d,
      media: { file, previewUrl, kind: "image" },
    }));
    // image_with_audio always needs an attached own track OR an
    // artist-provided clip. Keep user in the step; they pick a track
    // next.
  };

  // Final: upload (if not already) → POST /api/reels.
  const postReel = async () => {
    if (posting) return;
    const kind = draft.media?.kind;
    if (!draft.media) { toast.error("Pick media first"); return; }
    if (draft.reelType === "image_with_audio" && !draft.attachedTrack && !draft.artistAudio.enabled) {
      toast.error("Attach one of your RGM tracks"); return;
    }
    if (draft.artistAudio.enabled && !draft.artistAudio.ack) {
      toast.error("Please confirm the rights acknowledgement"); return;
    }
    setPosting(true);
    try {
      // Upload media if not already uploaded.
      let uploaded = draft.uploaded;
      if (!uploaded) {
        if (kind === "video") {
          const purpose = (draft.reelType === "recorded_freestyle" ||
                           draft.reelType === "performance_with_track" ||
                           draft.reelType === "video_with_track")
            ? "reel_recorded" : "reel_uploaded";
          uploaded = await uploadVideoFile(draft.media.file, purpose);
        } else {
          uploaded = await uploadImageFile(draft.media.file);
        }
        if (!uploaded?.url) { setPosting(false); return; }
      }

      // Upload artist-provided audio if enabled.
      let artistAudio = null;
      if (draft.artistAudio.enabled && draft.artistAudio.file) {
        const form = new FormData();
        form.append("file", draft.artistAudio.file);
        form.append("purpose", "general");
        const r = await api.post("/upload/audio", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        artistAudio = { url: r.data?.url, public_id: r.data?.public_id };
      }

      const payload = {
        reel_type: draft.reelType,
        media_url: uploaded.url,
        media_public_id: uploaded.public_id,
        duration_seconds: uploaded.duration_seconds || draft.media?.duration || null,
        poster_url: uploaded.poster_url || null,
        audio_source: draft.attachedTrack
          ? "own_track"
          : (artistAudio ? "artist_provided" : "none"),
        audio_track_id: draft.attachedTrack?.submission_id || null,
        audio_url: artistAudio?.url || null,
        audio_public_id: artistAudio?.public_id || null,
        audio_license_ack: !!(artistAudio && draft.artistAudio.ack),
        caption: (draft.caption || "").trim() || null,
        visibility: "public",
      };
      const r = await api.post("/reels", payload);
      setPostedReel(r.data?.reel || null);
      setStep(STEPS.DONE);
      toast.success("Reel posted");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string"
        ? detail
        : (detail?.message || "Could not post the Reel"));
    } finally {
      setPosting(false);
    }
  };

  // Lazy-load "my reels" after success so we can show the user their
  // new Reel on the confirmation screen.
  useEffect(() => {
    if (step !== STEPS.DONE) return;
    api.get("/reels/me", { params: { limit: 10 } })
      .then(r => setMyReels(r.data?.reels || []))
      .catch(() => setMyReels([]));
  }, [step]);

  // iPhone QA #5 — nudge Safari to preload the Review & Post preview
  // Blob so its native Play button is wired up before the user taps.
  useEffect(() => {
    if (step !== STEPS.PREVIEW) return;
    if (!previewVideoRef.current || draft.media?.kind !== "video") return;
    try { previewVideoRef.current.load(); } catch (_) { /* noop */ }
  }, [step, draft.media?.previewUrl, draft.media?.kind]);

  // ─────────────────────────────────────────────────────────────
  // Renderers
  // ─────────────────────────────────────────────────────────────
  const renderLauncher = () => (
    <div className="max-w-md mx-auto px-4" data-testid="create-reel-launcher">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-purple-300 font-black mb-1">RGM Reels</div>
        <h1 className="text-3xl font-black text-white">Create a Reel</h1>
        <p className="text-sm text-gray-400 mt-1">
          Mobile-first short-form video for your music.
          Free artists can record freestyles; premium unlocks promotional creator tools.
        </p>
      </div>
      <div className="grid grid-cols-1 gap-3">
        {TILE_DEFS.map(tile => {
          // Signed-out visitors see every tile as "Sign in"; signed-in
          // users see the real entitlement state. Freestyle is a free
          // feature so a signed-in free user sees it unlocked here.
          const unlocked = isSignedIn && hasFeature(tile.feature);
          const needsSignIn = !isSignedIn;
          return (
            <Card
              key={tile.key}
              onClick={() => pickMode(tile)}
              className={`relative cursor-pointer p-4 bg-black/60 border transition-colors
                ${unlocked
                  ? "border-purple-500/40 hover:border-purple-400"
                  : "border-gray-800 opacity-70"}`}
              data-testid={`reel-tile-${tile.key}`}
            >
              {!unlocked && !needsSignIn && <LockBadge onUpgrade={() => navigate("/membership?from=reels")} />}
              {needsSignIn && (
                <span
                  className="absolute top-2 right-2 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-purple-500/20 border border-purple-400/50 text-purple-100 font-black"
                  data-testid={`reel-tile-${tile.key}-signin-badge`}
                >Sign in</span>
              )}
              <div className="flex items-start gap-3">
                <div className="text-3xl">{tile.icon}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-white font-black text-lg">{tile.title}</div>
                  <div className="text-sm text-gray-400">{tile.sub}</div>
                  {!unlocked && !needsSignIn && (
                    <div className="text-xs mt-2 text-yellow-300" data-testid={`reel-tile-${tile.key}-lock-msg`}>
                      Unlocks with RGM Premium
                    </div>
                  )}
                  {needsSignIn && (
                    <div className="text-xs mt-2 text-purple-200" data-testid={`reel-tile-${tile.key}-signin-msg`}>
                      Sign in to create
                    </div>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
      <div className="mt-6 text-[11px] text-gray-500" data-testid="tier-footer">
        Current plan: <span className="font-black text-gray-400">{tier.replace("_", " ")}</span>
      </div>
    </div>
  );

  const renderUploadVideo = () => (
    <div className="max-w-md mx-auto px-4" data-testid="reel-upload-video-step">
      <h2 className="text-white text-xl font-black mb-3">Upload a video</h2>
      <input
        type="file"
        accept="video/mp4,video/quicktime,video/webm,video/x-m4v"
        onChange={(e) => onUploadVideoChosen(e.target.files?.[0])}
        className="block w-full text-white file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-purple-600 file:text-white file:font-black"
        data-testid="reel-upload-video-input"
      />
      <p className="text-xs text-gray-500 mt-2">MP4, MOV, WEBM or M4V — up to 150 MB.</p>
      <div className="mt-4">
        <Button variant="outline" onClick={goHome} className="text-white border-gray-700" data-testid="reel-upload-video-cancel">Cancel</Button>
      </div>
    </div>
  );

  const renderPhotoMusic = () => (
    <div className="max-w-md mx-auto px-4" data-testid="reel-photo-music-step">
      <h2 className="text-white text-xl font-black mb-3">Photo + music</h2>
      {!draft.media && (
        <>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={(e) => onUploadImageChosen(e.target.files?.[0])}
            className="block w-full text-white file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-purple-600 file:text-white file:font-black"
            data-testid="reel-photo-input"
          />
          <p className="text-xs text-gray-500 mt-2">JPG, PNG or WebP — up to 10 MB.</p>
          <div className="mt-4">
            <Button variant="outline" onClick={goHome} className="text-white border-gray-700">Cancel</Button>
          </div>
        </>
      )}
      {draft.media && !draft.attachedTrack && (
        <>
          <img src={draft.media.previewUrl} alt="" className="w-full rounded-lg mb-3 max-h-80 object-cover" data-testid="reel-photo-preview" />
          <MyTracksPicker
            onPick={(t) => { setDraft(d => ({ ...d, attachedTrack: t })); setStep(STEPS.PREVIEW); }}
            onCancel={() => setDraft(d => ({ ...d, media: null }))}
          />
        </>
      )}
    </div>
  );

  const renderPreview = () => {
    const needsAck = draft.artistAudio.enabled;
    const canPost = draft.media && !uploading && !posting
      && (!needsAck || draft.artistAudio.ack);
    return (
      <div className="max-w-md mx-auto px-4" data-testid="reel-preview-step">
        <h2 className="text-white text-xl font-black mb-3">Review &amp; post</h2>
        <div className="rounded-xl overflow-hidden border border-purple-500/30 bg-black">
          {draft.media?.kind === "video" ? (
            <video
              ref={previewVideoRef}
              src={draft.media.previewUrl}
              controls
              playsInline
              preload="auto"
              // iPhone QA #5 — Review & Post preview mirrors the
              // recorder-review treatment: Safari's native controls
              // handle Play on iPhone correctly ONLY when the three
              // defensive attributes from QA #1 are NOT applied
              // (disablePictureInPicture / controlsList=…noremoteplayback
              // / x-webkit-airplay=deny). The Blob is now a complete
              // single-fragment MP4, so the InvalidStateError PiP
              // race that originally motivated those attributes is
              // no longer reproducible.
              onLoadedMetadata={(e) => {
                if (process.env.NODE_ENV !== "production") {
                  const v = e.target;
                  try { console.log("[reel-preview] loadedmetadata", {
                    videoWidth: v.videoWidth, videoHeight: v.videoHeight,
                    duration: v.duration, readyState: v.readyState,
                    networkState: v.networkState,
                  }); } catch (_) {/*noop*/}
                }
              }}
              onError={(e) => {
                if (process.env.NODE_ENV !== "production") {
                  const v = e.target;
                  try { console.log("[reel-preview] error", {
                    code: v.error?.code, message: v.error?.message,
                  }); } catch (_) {/*noop*/}
                }
              }}
              className="w-full max-h-[min(60vh,460px)] object-contain bg-black"
              data-testid="reel-preview-video"
            />
          ) : (
            <img
              src={draft.media?.previewUrl}
              alt=""
              className="w-full max-h-[min(60vh,460px)] object-contain bg-black"
              data-testid="reel-preview-image"
            />
          )}
        </div>
        {draft.attachedTrack && (
          <div
            className="mt-3 flex items-center gap-3 bg-black/50 border border-gray-800 rounded-lg p-3"
            data-testid="reel-attached-track"
          >
            <div className="w-10 h-10 rounded bg-gray-900 overflow-hidden flex-shrink-0">
              {draft.attachedTrack.cover_art
                ? <img src={draft.attachedTrack.cover_art} alt="" className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center text-gray-600">♪</div>}
            </div>
            <div className="flex-1 text-white text-sm">
              <div className="font-bold truncate">{draft.attachedTrack.track_title || "Untitled"}</div>
              <div className="text-xs text-gray-400">Your RGM track</div>
            </div>
          </div>
        )}
        {/* Artist-provided audio opt-in (freestyle + uploaded_video only). */}
        {["recorded_freestyle", "uploaded_video"].includes(draft.reelType) && (
          <div className="mt-3 bg-black/40 border border-gray-800 rounded-lg p-3">
            <label className="flex items-center gap-2 text-sm text-white">
              <input
                type="checkbox"
                checked={draft.artistAudio.enabled}
                onChange={(e) => setDraft(d => ({
                  ...d, artistAudio: { ...d.artistAudio, enabled: e.target.checked, ack: false },
                }))}
                data-testid="reel-artist-audio-toggle"
              />
              Attach my own audio file
            </label>
            {draft.artistAudio.enabled && (
              <div className="mt-2 space-y-2">
                <input
                  type="file"
                  accept="audio/mpeg,audio/mp3,audio/wav"
                  onChange={(e) => setDraft(d => ({
                    ...d, artistAudio: { ...d.artistAudio, file: e.target.files?.[0] || null },
                  }))}
                  className="block w-full text-white text-xs"
                  data-testid="reel-artist-audio-input"
                />
                <label className="flex items-start gap-2 text-xs text-gray-300">
                  <input
                    type="checkbox"
                    checked={draft.artistAudio.ack}
                    onChange={(e) => setDraft(d => ({
                      ...d, artistAudio: { ...d.artistAudio, ack: e.target.checked },
                    }))}
                    data-testid="reel-artist-audio-ack"
                  />
                  I own or have permission to use this audio.
                </label>
              </div>
            )}
          </div>
        )}
        <textarea
          value={draft.caption}
          onChange={(e) => setDraft(d => ({ ...d, caption: e.target.value }))}
          placeholder="Caption (optional, max 300 chars)"
          maxLength={300}
          rows={2}
          className="w-full mt-3 bg-gray-900 border border-gray-800 rounded-md p-2 text-white text-sm"
          data-testid="reel-caption-input"
        />
        {(uploading || posting) && (
          <div className="mt-2 text-xs text-gray-400" data-testid="reel-posting-status">
            {uploading ? `Uploading… ${uploadPct}%` : "Posting…"}
          </div>
        )}
        <div className="flex gap-2 mt-4">
          <Button
            variant="outline"
            onClick={goHome}
            disabled={posting}
            className="text-white border-gray-700"
            data-testid="reel-preview-cancel"
          >Cancel</Button>
          <Button
            onClick={postReel}
            disabled={!canPost}
            className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-black"
            data-testid="reel-post-button"
          >{posting ? "Posting…" : "Post Reel"}</Button>
        </div>
      </div>
    );
  };

  const renderDone = () => (
    <div className="max-w-md mx-auto px-4 text-center" data-testid="reel-done-step">
      <div className="text-6xl mb-2">🎉</div>
      <h2 className="text-white text-2xl font-black">Reel posted</h2>
      <p className="text-gray-400 text-sm mt-1">
        Your Reel is live for RGM listeners.
      </p>
      {postedReel && (
        <div className="mt-4 text-xs text-gray-500" data-testid="reel-done-id">
          Reel ID: <span className="text-gray-300">{postedReel.reel_id}</span>
        </div>
      )}
      <div className="flex gap-2 mt-5 justify-center">
        <Button onClick={() => { reset(); setStep(STEPS.PICK_MODE); setMode(null); }}
                className="bg-purple-600 hover:bg-purple-500 text-white"
                data-testid="reel-done-create-another">
          Create another
        </Button>
        <Button variant="outline" onClick={() => navigate("/dashboard")}
                className="text-white border-gray-700"
                data-testid="reel-done-dashboard">
          Done
        </Button>
      </div>
      {myReels && myReels.length > 0 && (
        <div className="mt-6 text-left" data-testid="reel-done-my-reels">
          <div className="text-xs uppercase tracking-widest text-gray-500 mb-2">Your recent Reels</div>
          <div className="grid grid-cols-3 gap-2">
            {myReels.slice(0, 6).map(r => (
              <button
                key={r.reel_id}
                type="button"
                onClick={() => navigate(`/reels?reel_id=${encodeURIComponent(r.reel_id)}`)}
                className="aspect-[9/16] bg-black/60 border border-gray-800 rounded overflow-hidden"
                data-testid={`reel-done-thumb-${r.reel_id}`}
              >
                {r.poster_url
                  ? <img src={r.poster_url} alt="" className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center text-gray-600 text-xs">🎬</div>}
              </button>
            ))}
          </div>
          <div className="mt-3">
            <Button
              variant="outline"
              onClick={() => navigate("/reels")}
              className="text-white border-gray-700 w-full"
              data-testid="reel-done-view-feed"
            >
              Open Reels feed
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  // ─────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#0a0a14] py-8">
      {step === STEPS.PICK_MODE && renderLauncher()}
      {step === STEPS.FREESTYLE && (
        <FreestyleRecorder
          onFinish={onFreestyleFinish}
          onCancel={goHome}
        />
      )}
      {step === STEPS.USE_MY_TRACK_PICK && (
        <MyTracksPicker
          onPick={(t) => { setDraft(d => ({ ...d, attachedTrack: t })); setStep(STEPS.USE_MY_TRACK_CAPTURE); }}
          onCancel={goHome}
        />
      )}
      {step === STEPS.USE_MY_TRACK_CAPTURE && (
        <UseMyTrackCapture
          onRecord={() => setStep(STEPS.FREESTYLE)}
          onUpload={() => setStep(STEPS.UPLOAD_VIDEO)}
          onCancel={goHome}
        />
      )}
      {step === STEPS.UPLOAD_VIDEO && renderUploadVideo()}
      {step === STEPS.PHOTO_MUSIC && renderPhotoMusic()}
      {step === STEPS.PREVIEW && renderPreview()}
      {step === STEPS.DONE && renderDone()}
    </div>
  );
}

// Simple two-choice picker shown after a creator picks their own RGM
// track. Reuses the same freestyle recorder and uploader downstream.
function UseMyTrackCapture({ onRecord, onUpload, onCancel }) {
  return (
    <div className="max-w-md mx-auto px-4" data-testid="use-my-track-capture-step">
      <h2 className="text-white text-xl font-black mb-3">Create your Reel</h2>
      <p className="text-sm text-gray-400 mb-4">
        You'll perform over your RGM track, or upload an existing clip and
        attach it as the audio.
      </p>
      <div className="grid grid-cols-1 gap-3">
        <Button onClick={onRecord} className="bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-400 hover:to-pink-500 text-white font-black py-6" data-testid="use-my-track-record">
          🎤 Record Video
        </Button>
        <Button onClick={onUpload} variant="outline" className="text-white border-gray-700 py-6" data-testid="use-my-track-upload">
          📱 Upload Video
        </Button>
      </div>
      <div className="mt-4">
        <Button variant="outline" onClick={onCancel} className="text-white border-gray-700" data-testid="use-my-track-cancel-step">
          Back
        </Button>
      </div>
    </div>
  );
}
